import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  ChevronRight, Mail, FileText, CheckCircle2, XCircle, Clock, CreditCard,
  Send, ArrowRight, Eye, AlertCircle, Download, Columns, List as ListIcon,
  UserCircle2,
} from 'lucide-react';
import { UserRecapSheet } from './UserRecapSheet';

const PIPELINE = [
  { key: 'submitted',            label: 'Demande',              icon: Mail },
  { key: 'account_created',      label: 'Compte créé',          icon: Send },
  { key: 'dossier_in_progress',  label: 'Dossier en cours',     icon: FileText },
  { key: 'school_review',        label: 'Validation école',     icon: Eye },
  { key: 'funder_review',        label: 'Validation financeur', icon: AlertCircle },
  { key: 'payment_pending',      label: 'En attente règlement', icon: CreditCard },
  { key: 'confirmed',            label: 'Inscription validée',  icon: CheckCircle2 },
];
const STATUS_LABELS = Object.fromEntries(PIPELINE.map((p) => [p.key, p.label]));
STATUS_LABELS.rejected = 'Refusée';

const STATUS_TINTS = {
  submitted: 'bg-[#1F2A4A]/10 text-[#1F2A4A]',
  account_created: 'bg-amber-500/15 text-amber-700',
  dossier_in_progress: 'bg-amber-500/15 text-amber-700',
  school_review: 'bg-amber-500/15 text-amber-700',
  funder_review: 'bg-amber-500/15 text-amber-700',
  payment_pending: 'bg-amber-500/15 text-amber-700',
  confirmed: 'bg-emerald-500/15 text-emerald-700',
  rejected: 'bg-red-500/10 text-red-600',
};

const PAYMENT_METHODS = [
  { value: 'stripe',           label: 'Stripe (CB)' },
  { value: 'paypal',           label: 'PayPal' },
  { value: 'offline_opco',     label: 'OPCO (virement)' },
  { value: 'offline_transfer', label: 'Virement bancaire' },
  { value: 'offline_check',    label: 'Chèque' },
  { value: 'offline_other',    label: 'Autre' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (_) { return iso; }
};

// ─────────────────────────────────────────── Pipeline tracker
const PipelineTrack = ({ current }) => {
  const idx = PIPELINE.findIndex((p) => p.key === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="pipeline-track">
      {PIPELINE.map((step, i) => {
        const Icon = step.icon;
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={step.key} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-medium transition-all ${
              active ? 'bg-[#C9B896] text-[#1F2A4A] shadow-sm' :
              done ? 'bg-emerald-500/15 text-emerald-700' :
              'bg-[#1F2A4A]/5 text-[#1F2A4A]/40'
            }`}>
              <Icon className="w-3 h-3" />
              {step.label}
            </div>
            {i < PIPELINE.length - 1 && <ChevronRight className="w-3 h-3 text-[#1F2A4A]/20 mx-0.5" />}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────── Detail dialog
const ApplicationDetailDialog = ({ app, open, onClose, onChanged, onOpenFicheClient }) => {
  const [requirements, setRequirements] = useState([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payment, setPayment] = useState({ method: 'offline_opco', amount: '', reference: '', note: '' });

  useEffect(() => {
    if (open) api.getDossierRequirements().then((d) => setRequirements(d.requirements || [])).catch(() => {});
  }, [open]);

  if (!app) return null;
  const docs = app.documents || [];

  const transition = async (newStatus) => {
    try {
      await api.adminTransitionApplication(app.id, newStatus);
      toast.success(`Statut → ${STATUS_LABELS[newStatus]}`);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const resendMagic = async () => {
    try {
      const r = await api.adminResendMagicLink(app.id);
      toast.success("Lien magique envoyé par email");
      // Show the link for manual copy if SMTP is off
      if (r.link) navigator.clipboard?.writeText(window.location.origin + r.link).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const validateDoc = async (key, reason = null) => {
    try {
      await api.adminValidateDocument(app.id, key, reason);
      toast.success(reason ? "Document refusé" : "Document validé");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const downloadDoc = async (key, suggestedName) => {
    try {
      const blob = await api.downloadDossierDocument(app.id, key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName || `${key}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || "Téléchargement impossible"); }
  };

  const markPaid = async () => {
    if (!payment.amount) { toast.error("Indique un montant"); return; }
    try {
      await api.adminMarkPaid(app.id, {
        method: payment.method,
        amount: parseFloat(payment.amount),
        reference: payment.reference,
        note: payment.note,
      });
      toast.success("Règlement enregistré · inscription validée");
      setPaymentOpen(false);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const idx = PIPELINE.findIndex((p) => p.key === app.status);
  const nextStep = idx >= 0 && idx < PIPELINE.length - 1 ? PIPELINE[idx + 1] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-white" data-testid="dossier-detail-dialog">
        <DialogHeader>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">DOSSIER</p>
          <DialogTitle className="font-serif text-2xl">
            {app.user?.first_name} {app.user?.last_name}
            <span className="ml-3 text-base text-[#1F2A4A]/50">· {app.course?.title}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3 text-xs">
            <span>{app.user?.email}</span>
            {app.user?.phone && <span>· {app.user.phone}</span>}
            <span>· Déposée le {fmtDate(app.created_at)}</span>
          </DialogDescription>
          {/* Quick access to full client record */}
          {app.user_id && onOpenFicheClient && (
            <div className="pt-3">
              <Button
                onClick={() => onOpenFicheClient(app.user_id)}
                variant="outline"
                className="rounded-full border-[#C9B896] text-[#1F2A4A] hover:bg-[#C9B896]/15"
                data-testid="open-fiche-client-from-dialog"
              >
                <UserCircle2 className="w-4 h-4 mr-2" />
                Voir la fiche client complète
              </Button>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <PipelineTrack current={app.status} />

          {/* Next-step actions */}
          <div className="bg-[#FAF7F0]/60 rounded-xl p-5 flex flex-wrap items-center gap-3">
            {app.status === 'submitted' || app.status === 'account_created' ? (
              <Button onClick={resendMagic} variant="outline" className="rounded-full" data-testid="resend-magic-link">
                <Send className="w-4 h-4 mr-2" /> Envoyer / renvoyer le lien magique
              </Button>
            ) : null}
            {nextStep && (
              <Button onClick={() => transition(nextStep.key)} className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full" data-testid={`transition-${nextStep.key}`}>
                Faire passer en « {nextStep.label} » <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {app.status === 'payment_pending' && (
              <Button onClick={() => setPaymentOpen(true)} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full" data-testid="mark-paid">
                <CreditCard className="w-4 h-4 mr-2" /> Enregistrer un règlement
              </Button>
            )}
            {app.status !== 'rejected' && (
              <Button onClick={() => transition('rejected')} variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full" data-testid="reject-application">
                <XCircle className="w-4 h-4 mr-2" /> Refuser
              </Button>
            )}
            {/* Manual status override */}
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs text-[#1F2A4A]/60">Forcer :</Label>
              <Select value={app.status} onValueChange={transition}>
                <SelectTrigger className="w-44 h-8 text-xs bg-white" data-testid="status-override">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...PIPELINE.map((p) => p.key), 'rejected'].map((k) => (
                    <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Documents */}
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">PIÈCES DU DOSSIER</p>
            <div className="space-y-2">
              {requirements.map((req) => {
                const doc = docs.find((d) => d.key === req.key);
                const status = doc?.status || 'missing';
                return (
                  <div key={req.key} className="flex items-center gap-3 p-3 bg-white border border-[#1F2A4A]/10 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1F2A4A]">{req.label}</div>
                      <div className="text-[10px] text-[#1F2A4A]/40">
                        {req.required ? 'Obligatoire' : 'Optionnel'}
                        {doc?.file_name && <> · <span className="font-mono">{doc.file_name}</span></>}
                        {doc?.rejection_reason && <> · <span className="text-red-500">Refusé : {doc.rejection_reason}</span></>}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium ${
                      status === 'validated' ? 'bg-emerald-500/15 text-emerald-700' :
                      status === 'uploaded' ? 'bg-amber-500/15 text-amber-700' :
                      status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                      'bg-[#1F2A4A]/8 text-[#1F2A4A]/50'
                    }`}>
                      {status === 'validated' ? 'Validé' : status === 'uploaded' ? 'À valider' : status === 'rejected' ? 'Refusé' : 'Manquant'}
                    </span>
                    {doc?.file_path && (
                      <>
                        <button
                          onClick={() => downloadDoc(req.key, doc.file_name)}
                          title="Télécharger"
                          className="text-[#1F2A4A]/70 hover:text-[#C9B896] transition-colors"
                          data-testid={`doc-download-${req.key}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {status !== 'validated' && (
                          <button onClick={() => validateDoc(req.key, null)} title="Valider" className="text-emerald-600 hover:text-emerald-800 transition-colors" data-testid={`doc-validate-${req.key}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        {status !== 'rejected' && (
                          <button onClick={() => {
                            const r = window.prompt("Motif de refus :");
                            if (r) validateDoc(req.key, r);
                          }} title="Refuser" className="text-red-500 hover:text-red-700 transition-colors" data-testid={`doc-reject-${req.key}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status history */}
          {app.status_history?.length > 0 && (
            <details className="bg-[#FAF7F0]/40 rounded-xl p-4">
              <summary className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] cursor-pointer">HISTORIQUE</summary>
              <ul className="mt-3 space-y-2 text-xs text-[#1F2A4A]/70">
                {app.status_history.slice().reverse().map((h, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{STATUS_LABELS[h.from] || h.from} → <strong>{STATUS_LABELS[h.to] || h.to}</strong></span>
                    <span className="text-[#1F2A4A]/40">{fmtDate(h.at)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Payment dialog */}
        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Enregistrer un règlement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Méthode</Label>
                <Select value={payment.method} onValueChange={(m) => setPayment({ ...payment, method: m })}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Montant (€)</Label>
                <Input type="number" step="0.01" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} className="mt-1" data-testid="payment-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Référence (optionnel)</Label>
                <Input value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} placeholder="N° virement, OPCO ref, etc." className="mt-1" data-testid="payment-reference" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Note</Label>
                <Textarea value={payment.note} onChange={(e) => setPayment({ ...payment, note: e.target.value })} className="mt-1 min-h-[60px]" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentOpen(false)} className="rounded-full">Annuler</Button>
                <Button onClick={markPaid} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full" data-testid="payment-confirm">
                  Valider l'inscription
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────── Pipeline list
export const DossierPipelineAdmin = () => {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ficheUserId, setFicheUserId] = useState(null);

  // Custom draggable scrollbar (always visible) for the kanban grid
  const kanbanRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState({ scrollWidth: 0, clientWidth: 0, scrollLeft: 0 });

  useLayoutEffect(() => {
    if (view !== 'kanban') return undefined;
    const el = kanbanRef.current;
    if (!el) return undefined;
    const measure = () => setScrollMetrics({ scrollWidth: el.scrollWidth, clientWidth: el.clientWidth, scrollLeft: el.scrollLeft });
    measure();
    const onScroll = () => setScrollMetrics((m) => ({ ...m, scrollLeft: el.scrollLeft }));
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [view, apps]);

  const startThumbDrag = (trackEl) => (e) => {
    e.preventDefault();
    const kanban = kanbanRef.current;
    if (!kanban || !trackEl) return;
    const trackRect = trackEl.getBoundingClientRect();
    const { scrollWidth, clientWidth } = kanban;
    const ratio = clientWidth / scrollWidth;
    const thumbWidth = Math.max(40, trackRect.width * ratio);
    const usableTrack = trackRect.width - thumbWidth;
    const maxScroll = scrollWidth - clientWidth;
    const startX = e.clientX;
    const startScroll = kanban.scrollLeft;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dxRatio = dx / usableTrack;
      kanban.scrollLeft = Math.max(0, Math.min(maxScroll, startScroll + dxRatio * maxScroll));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onTrackClick = (trackEl) => (e) => {
    if (e.target !== trackEl) return; // ignore clicks on the thumb
    const kanban = kanbanRef.current;
    if (!kanban) return;
    const trackRect = trackEl.getBoundingClientRect();
    const { scrollWidth, clientWidth } = kanban;
    const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
    kanban.scrollLeft = clickRatio * (scrollWidth - clientWidth);
  };

  const hasOverflow = scrollMetrics.scrollWidth > scrollMetrics.clientWidth + 4;
  const thumbWidthPct = hasOverflow ? Math.max(8, (scrollMetrics.clientWidth / scrollMetrics.scrollWidth) * 100) : 100;
  const maxScroll = Math.max(1, scrollMetrics.scrollWidth - scrollMetrics.clientWidth);
  const thumbLeftPct = hasOverflow ? (scrollMetrics.scrollLeft / maxScroll) * (100 - thumbWidthPct) : 0;

  const load = async () => {
    setLoading(true);
    try {
      const data = await import('../../lib/api').then((m) => m.api).then((apiClient) => apiClient);
      const axios = (await import('axios')).default;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/applications`;
      const res = await axios.get(url);
      setApps(res.data || []);
    } catch (_) { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return apps;
    if (filter === 'active') return apps.filter((a) => !['confirmed', 'rejected'].includes(a.status));
    return apps.filter((a) => a.status === filter);
  }, [apps, filter]);

  const counts = useMemo(() => {
    const c = { all: apps.length, active: 0 };
    for (const a of apps) {
      c[a.status] = (c[a.status] || 0) + 1;
      if (!['confirmed', 'rejected'].includes(a.status)) c.active += 1;
    }
    return c;
  }, [apps]);

  const refresh = async () => {
    await load();
    if (selected) {
      const updated = apps.find((a) => a.id === selected.id) || null;
      setSelected(updated);
    }
  };

  return (
    <div className="space-y-6" data-testid="dossier-pipeline-admin">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">CANDIDATURES</p>
          <p className="text-sm text-[#1F2A4A]/55 mt-1">Suivi du parcours candidat de la demande à la validation.</p>
        </div>
        {/* View toggle */}
        <div className="inline-flex items-center gap-1 bg-[#1F2A4A]/5 rounded-full p-1 self-start md:self-end">
          <button
            onClick={() => setView('kanban')}
            data-testid="view-kanban"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all ${
              view === 'kanban' ? 'bg-white text-[#1F2A4A] shadow-sm' : 'text-[#1F2A4A]/55 hover:text-[#1F2A4A]'
            }`}
          >
            <Columns className="w-3.5 h-3.5" /> Colonnes
          </button>
          <button
            onClick={() => setView('list')}
            data-testid="view-list"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all ${
              view === 'list' ? 'bg-white text-[#1F2A4A] shadow-sm' : 'text-[#1F2A4A]/55 hover:text-[#1F2A4A]'
            }`}
          >
            <ListIcon className="w-3.5 h-3.5" /> Liste
          </button>
        </div>
      </div>

      {/* Filters — hidden in kanban view since columns already group statuses */}
      {view === 'list' && (
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: `Toutes (${counts.all || 0})` },
            { key: 'active', label: `Actives (${counts.active || 0})` },
            ...PIPELINE.map((p) => ({ key: p.key, label: `${p.label} (${counts[p.key] || 0})` })),
            { key: 'rejected', label: `Refusées (${counts.rejected || 0})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`pipeline-filter-${f.key}`}
              className={`text-xs px-4 py-2 rounded-full transition-all ${
                filter === f.key ? 'bg-[#1F2A4A] text-[#E8DCC8] font-medium shadow-sm' : 'bg-[#1F2A4A]/5 text-[#1F2A4A]/60 hover:bg-[#1F2A4A]/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-[#1F2A4A]/40 text-sm">Chargement…</div>
      ) : apps.length === 0 ? (
        <div className="p-16 bg-white border border-[#1F2A4A]/10 rounded-2xl text-center text-sm text-[#1F2A4A]/40 italic">
          Aucune candidature pour le moment.
        </div>
      ) : view === 'kanban' ? (
        /* Kanban with always-visible draggable scrollbars (top + bottom) */
        <div className="relative">
          <style>{`
            [data-testid="dossier-kanban"]::-webkit-scrollbar { display: none; }
          `}</style>
          {/* TOP custom scrollbar */}
          <div
            onClick={(e) => onTrackClick(e.currentTarget)(e)}
            className="relative h-3 bg-[#1F2A4A]/8 rounded-full mb-2 cursor-pointer select-none"
            data-testid="kanban-scrollbar-top"
          >
            <div
              onMouseDown={(e) => startThumbDrag(e.currentTarget.parentElement)(e)}
              className="absolute top-0 bottom-0 bg-[#C9B896] hover:bg-[#B8A57E] active:bg-[#A89164] rounded-full transition-colors cursor-grab active:cursor-grabbing shadow-sm"
              style={{ left: `${thumbLeftPct}%`, width: `${thumbWidthPct}%`, minWidth: 40 }}
              data-testid="kanban-scrollbar-top-thumb"
            />
          </div>

          <div
            ref={kanbanRef}
            className="grid gap-3 overflow-x-auto overflow-y-hidden pb-1 scrollbar-hide"
            style={{ gridTemplateColumns: `repeat(${PIPELINE.length + 1}, minmax(260px, 1fr))`, scrollbarWidth: 'none' }}
            data-testid="dossier-kanban"
          >
            {[...PIPELINE, { key: 'rejected', label: 'Refusées', icon: XCircle }].map((col) => {
              const Icon = col.icon;
              const colApps = apps.filter((a) => a.status === col.key);
              const tint = STATUS_TINTS[col.key] || 'bg-[#1F2A4A]/8 text-[#1F2A4A]/60';
              return (
                <div
                  key={col.key}
                  className="bg-[#FAF7F0]/40 border border-[#1F2A4A]/8 rounded-2xl p-3 flex flex-col min-h-[420px]"
                  data-testid={`kanban-col-${col.key}`}
                >
                  <header className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center ${tint}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#1F2A4A]">
                        {col.label}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-[#1F2A4A]/45">{colApps.length}</span>
                  </header>
                  <div className="flex-1 space-y-2 overflow-y-auto">
                    {colApps.length === 0 ? (
                      <p className="text-[11px] text-[#1F2A4A]/30 italic px-1 py-3 text-center">Vide</p>
                    ) : (
                      colApps.map((app) => (
                        <button
                          key={app.id}
                          onClick={() => setSelected(app)}
                          data-testid={`kanban-card-${app.id}`}
                          className="w-full bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896]/60 hover:shadow-md rounded-xl p-3 text-left transition-all group"
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center text-xs font-medium shrink-0">
                              {(app.user?.first_name?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1F2A4A] truncate">
                                {app.user?.first_name} {app.user?.last_name}
                              </p>
                              <p className="text-[10px] text-[#1F2A4A]/50 truncate mt-0.5">
                                {app.course?.title}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1F2A4A]/8">
                            <span className="text-[10px] text-[#1F2A4A]/45">{fmtDate(app.created_at)}</span>
                            {app.documents?.length > 0 && (
                              <span className="text-[10px] font-mono text-[#C9B896]">
                                {app.documents.filter(d => d.status === 'validated').length}/{app.documents.length} docs
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* BOTTOM custom scrollbar */}
          <div
            onClick={(e) => onTrackClick(e.currentTarget)(e)}
            className="relative h-3 bg-[#1F2A4A]/8 rounded-full mt-2 cursor-pointer select-none"
            data-testid="kanban-scrollbar-bottom"
          >
            <div
              onMouseDown={(e) => startThumbDrag(e.currentTarget.parentElement)(e)}
              className="absolute top-0 bottom-0 bg-[#C9B896] hover:bg-[#B8A57E] active:bg-[#A89164] rounded-full transition-colors cursor-grab active:cursor-grabbing shadow-sm"
              style={{ left: `${thumbLeftPct}%`, width: `${thumbWidthPct}%`, minWidth: 40 }}
              data-testid="kanban-scrollbar-bottom-thumb"
            />
          </div>
        </div>
      ) : (
        /* List view */
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="p-16 bg-white border border-[#1F2A4A]/10 rounded-2xl text-center text-sm text-[#1F2A4A]/40 italic">
              Aucune candidature dans cette catégorie.
            </div>
          ) : (
            filtered.map((app) => (
              <button
                key={app.id}
                onClick={() => setSelected(app)}
                data-testid={`pipeline-row-${app.id}`}
                className="w-full bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896]/40 rounded-xl p-4 transition-all text-left flex items-center gap-4 group"
              >
                <div className="w-10 h-10 rounded-full bg-[#C9B896]/20 text-[#1F2A4A] flex items-center justify-center font-medium shrink-0">
                  {(app.user?.first_name?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1F2A4A] truncate">
                    {app.user?.first_name} {app.user?.last_name}
                    <span className="ml-2 text-xs text-[#1F2A4A]/40">· {app.course?.title}</span>
                  </div>
                  <div className="text-[11px] text-[#1F2A4A]/50 mt-0.5">
                    {app.user?.email} · déposée le {fmtDate(app.created_at)}
                    {app.documents?.length > 0 && <> · {app.documents.filter(d => d.status === 'validated').length}/{app.documents.length} docs validés</>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium shrink-0 ${STATUS_TINTS[app.status] || 'bg-[#1F2A4A]/8 text-[#1F2A4A]/60'}`}>
                  {STATUS_LABELS[app.status] || app.status}
                </span>
                <ChevronRight className="w-4 h-4 text-[#1F2A4A]/30 group-hover:text-[#C9B896] transition-colors shrink-0" />
              </button>
            ))
          )}
        </div>
      )}

      <ApplicationDetailDialog
        app={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onChanged={refresh}
        onOpenFicheClient={(uid) => setFicheUserId(uid)}
      />

      {ficheUserId && (
        <UserRecapSheet
          userId={ficheUserId}
          onClose={() => setFicheUserId(null)}
        />
      )}
    </div>
  );
};

export default DossierPipelineAdmin;
